/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 */
import { isDigit, isCharacter } from './common';

const ccFormats = [
    {type: 'amex', luhn: /^(?:3[47])/, pattern: /^(?:3[47][0-9]{13})$/}, // 34, 37
    {type: 'visa', luhn: /^(?:4)/, pattern: /^(?:4[0-9]{12}(?:[0-9]{1,6})?)$/}, // 4
    {type: 'mastercard', luhn: /^(?:(?:5[1-5])|(?:222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720))/, pattern: /^(?:(?:5[1-5][0-9]{14})|(?:222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12})$/}, // 51-55, 2221-2720
    {type: 'discover', luhn: /^(?:6011|65|64[4-9]|62[2-8])/, pattern: /^(?:6011[0-9]{12}|65[0-9]{14}|64[4-9][0-9]{13}|62([2-8][0-9]{13}))$/}, //6011, 622-628, 644-649, 65
    {type: 'diners', luhn: /^(?:3(?:0[0-5]|[68]))/, pattern: /^(?:3(?:0[0-5]|[68][0-9])[0-9]{11}(?:[0-9]{2})?)$/}, // 300-305, 36, 38
    {type: 'jcb', luhn: /^(?:2131|1800|(?:35(?:2[89]|[3-7][0-9]|8[0-9])))/, pattern: /^(?:(?:(?:2131|1800)[0-9]{11})|(?:35(?:2[89]|[3-7][0-9]|8[0-9])[0-9]{12}))$/} // 1800, 2131, 3528-3589
];


export const evaluateCreditCardNumber = (value) => {
    if (value && typeof value === 'string') {
        value = value.trim();
    }
    if (!value || value.length === 0 || typeof value !== 'string') {
        return {type: undefined, luhnValid: false, valid: false};
    }
    let type;
    let luhnValid = false;
    let valid = false;
    value = value.replace(' ', '');
    for (let n=0; n < ccFormats.length; n++) {
        let ccInfo = ccFormats[n];
        if (ccInfo.luhn.test(value)) {
            type = ccInfo.type;
            luhnValid = true;
            if (ccInfo.pattern.test(value)) {
                valid = true;
            }
            break;
        }
    }
    return {type, luhnValid, valid};
};

const isCardTokenized = (value) => {
    if (!value || typeof value !== 'string') {
        return false;
    }
    if (value.length < 8) {
        return false;
    }
    if (value.substring(0, 4) !== '****') {
        return false;
    }
    return /^[0-9]*$/i.test(value.substring(value.length-4));
};

/********************************/
/**  Validators                **/
/********************************/

export const required = (value) => {
    return value ? undefined : 'errors.required';
};

export const maxLength = (max) => (value) => {
    return value && value.length && value.length > max ? 'errors.maximum' : undefined;
};

export const minLength = (min) => (value) => {
    if (value === undefined || value === null || value.length === undefined) {
        return min > 0 ? 'errors.minimum' : undefined;
    }
    return value.length < min ? 'errors.minimum' : undefined;
};

export const number = (value) => {
    return value && isNaN(Number(value)) ? 'errors.NaN' : undefined;
};

export const greaterThan = (number) => (value) => {
    if (number === undefined || number === null || typeof number !== 'number') {
        return undefined;
    }
    return value && value <= number ? 'errors.greaterThan' : undefined;
};

export const greaterThanEqualTo = (number) => (value) => {
    if (number === undefined || number === null || typeof number !== 'number') {
        return undefined;
    }
    return value && value < number ? 'errors.greaterThanEqualTo' : undefined;
};

export const lessThan = (number) => (value) => {
    if (number === undefined || number === null || typeof number !== 'number') {
        return undefined;
    }
    return value && value >= number ? 'errors.lessThan' : undefined;
};

export const lessThanEqualTo = (number) => (value) => {
    if (number === undefined || number === null || typeof number !== 'number') {
        return undefined;
    }
    return value && value > number ? 'errors.lessThanEqualTo' : undefined;
};

export const alphanumeric = (value) => {
    return value && !/^[a-zA-Z0-9 ]*$/i.test(value) ? 'errors.alphaNumeric' : undefined;
};

export const alphabetic = (value) => {
    return value && !/^[a-zA-Z ]*$/i.test(value) ? 'errors.alphabetic' : undefined;
};

export const numeric = (value) => {
    return value && !/^[0-9]*$/i.test(value) ? 'errors.numeric' : undefined;
};

export const email = (value) => {
    return value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? 'errors.email' : undefined;
};

export const date = (value) => {
    return (!value || typeof value.getMonth !== 'function') ? 'errors.date' : undefined;
};

const formatsValidator = (formats, value, msg) => {
    if (!value) {
        return undefined;
    }
    if (!Array.isArray(formats)) {
        formats = [formats];
    }
    for (let n = 0; n < formats.length; n++) {
        if (formats[n].test(value)) {
            return undefined;
        }
    }
    return msg;
};

export const dateFormat = (formats) => (value) => {
    return formatsValidator(formats, value, 'errors.dateFormat');
};

export const phone = (formats) => (value) => {
    return formatsValidator(formats, value, 'errors.phone');
};

export const postalCode = (formats) => (value) => {
    return formatsValidator(formats, value, 'errors.postalCode');
};

export const currency = (formats) => (value) => {
    return formatsValidator(formats, value, 'errors.currency');
};

export const pattern = (formats) => (value) => {
    return formatsValidator(formats, value, 'errors.pattern');
};

export const string = (value) => {
    return value !== undefined && value !== null && typeof value === 'string' ? undefined : 'errors.string';
};

export const boolean = (value) => {
    return value !== undefined && value !== null && typeof value === 'boolean' ? undefined : 'errors.boolean';
};

export const array = (value) => {
    return Array.isArray(value) ? undefined : 'errors.array';
};

export const custom = (msg, func) => (value) => {
    return func(value, msg); 
};

export const creditCard = (value) => {
    if (!value) {
        return undefined;
    }
    if (isCardTokenized(value)) {
        return undefined;
    } 
    const ccInfo = evaluateCreditCardNumber(value);
    return ccInfo.valid ? undefined : 'errors.creditCard';
};


/********************************/
/**  Mask formatting           **/
/********************************/

const getMaskInfo = (mask) => {
    const index = mask.indexOf('[');
    if (index < 0) {
        return [mask];
    }
    const endIndex = mask.indexOf(']');
    if (endIndex < 0) {
        return [mask];
    }
    return [mask.substring(0, index), mask.substring(index+1, endIndex)]; 
};


export const maskedFormatter = (maskFormat, input, backspace, upperCase) => {
    if (!maskFormat || maskFormat.length === 0 || !input || input.length === 0) {
        return input;
    }
    const [base, optional] = getMaskInfo(maskFormat);
    const mask = base + (optional ? optional : '');
    let cleanFilter = mask.replace(/[XNA]/g, '');
    input = input.replace(new RegExp(`[${cleanFilter}]`, 'g'), '');

    let output = '';
    let index = 0;
    let code, len;
    for (let n = 0; n < mask.length; n++) {
        code = mask.charAt(n);
        len = output.length;
        if (code === 'X') {
            output += input.charAt(index) !== ' ' ? input.charAt(index++) : '';
        } else if (code === 'N') {
            output += isDigit(input.charAt(index)) ? input.charAt(index++) : '';
        } else if (code === 'A') {
            output += isCharacter(input.charAt(index)) ? input.charAt(index++) : '';
        } else {
            output += input.charAt(index) === code ? input.charAt(index++) : code;
        }
        if (output.length === len) {
            break;
        }
        if (index === input.length) {
            if (!backspace) {
                code = mask.charAt(n+1);
                if (code !== 'X' && code !== 'N' && code !== 'A' && (!optional || input.length !== base.length)) {
                    output += code;
                }
            }
            break;
        }
    }
    if (output.length > 0 && upperCase && (mask.includes('X') || mask.includes('A'))) {
        output = output.toUpperCase();
    }
    return output;
};

